/**
 * roxin-adaptive-header.js
 * ---------------------------------------------------------------------------
 * Faz o cabeçalho trocar de cor conforme o banner que está na tela.
 *
 *   banner alaranjado (perto de #E99E1D) -> header roxo,    texto laranja
 *   banner arroxeado  (perto de #61196F) -> header laranja, texto roxo
 *   qualquer outra cor                   -> padrão (roxo com texto laranja)
 *
 * ---------------------------------------------------------------------------
 * COMO A COR DO BANNER É DESCOBERTA
 *
 * A cor não está em nenhuma configuração do tema: está dentro do arquivo de
 * mídia. Então precisamos olhar o pixel.
 *
 * Funciona tanto para banner de IMAGEM quanto de VÍDEO. No caso do vídeo,
 * lemos a MINIATURA que a Shopify gera automaticamente (o mesmo quadro que
 * o tema já exibe enquanto o vídeo carrega) — assim não é preciso baixar
 * nem decodificar o vídeo para saber a cor da cena.
 *
 * Fazer isso na imagem em tamanho real seria caro (decodificar uma foto de
 * 3000px para ler a cor média). Em vez disso, pedimos à CDN da Shopify uma
 * versão de 20x20 da MESMA imagem — poucos bytes, decodificação instantânea —
 * e tiramos a média dela. É o mesmo arquivo, só que minúsculo.
 *
 * A leitura acontece uma vez por imagem e fica em cache. Se a página tiver o
 * mesmo banner repetido, ele é analisado uma vez só.
 *
 * ---------------------------------------------------------------------------
 * POR QUE `crossOrigin = 'anonymous'`
 *
 * Ler pixels de uma imagem de outro domínio "contamina" o canvas e o
 * navegador bloqueia a leitura. A CDN da Shopify envia os cabeçalhos de CORS
 * necessários, então pedimos a imagem em modo anônimo e a leitura é liberada.
 * Se por qualquer motivo falhar, caímos no esquema padrão — nunca quebra.
 *
 * ---------------------------------------------------------------------------
 * DESEMPENHO
 *
 * - Nada disso bloqueia a renderização: o script é `defer` e a análise roda
 *   depois, de forma assíncrona.
 * - Enquanto a cor não é conhecida, o cabeçalho já aparece com o esquema
 *   padrão. Não existe estado "sem cor", então não há piscada.
 * - A troca é feita mudando duas custom properties CSS, não recalculando
 *   layout.
 */
(() => {
  if (window.__roxinAdaptiveHeaderReady) return;
  window.__roxinAdaptiveHeaderReady = true;

  const AMOSTRA = 24; // largura em px da miniatura usada para ler a cor

  /** Converte "#61196f" em [r,g,b]. */
  const hexParaRgb = (hex) => {
    const h = String(hex || '').trim().replace('#', '');
    if (h.length !== 6) return null;
    const n = parseInt(h, 16);
    if (isNaN(n)) return null;
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  };

  /**
   * Distância entre duas cores.
   * Usamos uma aproximação ponderada em vez da distância simples em RGB:
   * o olho humano é bem mais sensível ao verde do que ao azul, e sem o peso
   * um roxo escuro e um marrom escuro seriam considerados "parecidos".
   */
  const distancia = (a, b) => {
    const rMedio = (a[0] + b[0]) / 2;
    const dr = a[0] - b[0];
    const dg = a[1] - b[1];
    const db = a[2] - b[2];
    return Math.sqrt(
      (2 + rMedio / 256) * dr * dr + 4 * dg * dg + (2 + (255 - rMedio) / 256) * db * db
    );
  };

  /** Troca a largura pedida na URL da CDN da Shopify (…&width=3000 -> &width=20). */
  const urlMiniatura = (src) => {
    try {
      const u = new URL(src, window.location.href);
      u.searchParams.set('width', String(AMOSTRA));
      u.searchParams.delete('height');
      return u.toString();
    } catch (e) {
      return src;
    }
  };

  const cache = new Map();

  /** Lê a cor média de uma imagem. Resolve com [r,g,b] ou null. */
  const corMedia = (src) => {
    if (!src) return Promise.resolve(null);
    if (cache.has(src)) return cache.get(src);

    const p = new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.decoding = 'async';

      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = AMOSTRA;
          canvas.height = AMOSTRA;
          const ctx = canvas.getContext('2d', { willReadFrequently: false });
          ctx.drawImage(img, 0, 0, AMOSTRA, AMOSTRA);
          const { data } = ctx.getImageData(0, 0, AMOSTRA, AMOSTRA);

          /*
            COR DOMINANTE, NÃO MÉDIA.

            A média falha justamente nos banners desta marca. Um fundo roxo
            com formas laranja grandes tem média num marrom "sujo" que não se
            parece com nenhuma das duas cores — e o cabeçalho caía no padrão
            mesmo com um banner claramente roxo.

            Aqui agrupamos as cores em faixas e pegamos a faixa mais
            frequente, que é o que o olho lê como "a cor do banner". Depois
            devolvemos a média real dos pixels daquela faixa, para não perder
            precisão com o arredondamento do agrupamento.
          */
          const faixas = new Map();

          for (let i = 0; i < data.length; i += 4) {
            if (data[i + 3] < 128) continue; // ignora pixels transparentes

            const r = data[i];
            const g = data[i + 1];
            const b = data[i + 2];
            const chave = ((r >> 5) << 10) | ((g >> 5) << 5) | (b >> 5);

            const atual = faixas.get(chave);
            if (atual) {
              atual.r += r;
              atual.g += g;
              atual.b += b;
              atual.n++;
            } else {
              faixas.set(chave, { r: r, g: g, b: b, n: 1 });
            }
          }

          let vencedora = null;
          faixas.forEach((v) => {
            if (!vencedora || v.n > vencedora.n) vencedora = v;
          });

          resolve(
            vencedora ? [vencedora.r / vencedora.n, vencedora.g / vencedora.n, vencedora.b / vencedora.n] : null
          );
        } catch (e) {
          resolve(null); // canvas bloqueado — segue no padrão
        }
      };

      img.onerror = () => resolve(null);
      img.src = urlMiniatura(src);
    });

    cache.set(src, p);
    return p;
  };

  const init = (root = document) => {
    const header = document.querySelector('[data-rx-adaptive]');
    if (!header || header.dataset.rxAdaptiveReady === 'true') return;
    header.dataset.rxAdaptiveReady = 'true';

    const alvoQuente = hexParaRgb(header.dataset.rxWarm);   // laranja da marca
    const alvoFrio = hexParaRgb(header.dataset.rxCool);     // roxo da marca
    const tolerancia = parseFloat(header.dataset.rxTolerance) || 140;
    if (!alvoQuente || !alvoFrio) return;

    /*
      A BARRA DE ANÚNCIOS É OUTRA SEÇÃO.

      Ela fica fora do <header-component> e não herda nada dele: nem as
      variáveis de cor, nem o atributo de esquema. Para que acompanhe o
      cabeçalho sem duplicar a paleta num segundo grupo de configurações,
      publicamos aqui, no <html>, as duas cores que o cabeçalho recebeu por
      schema. O CSS de cada seção decide o que fazer com elas.

      A paleta e o atributo de esquema são escritos sempre juntos (ver
      `aplicar`), então nunca existe um estado em que o esquema está definido
      e as cores não.
    */
    const raiz = document.documentElement;
    const publicarCor = (nome, hex, rgb) => {
      raiz.style.setProperty(nome, String(hex).trim());
      raiz.style.setProperty(nome + '-rgb', rgb.map(Math.round).join(' '));
    };
    publicarCor('--rx-warm', header.dataset.rxWarm, alvoQuente);
    publicarCor('--rx-cool', header.dataset.rxCool, alvoFrio);

    const aplicar = (esquema) => {
      // 'warm'    = banner laranja -> header roxo,    barra de anúncios laranja
      // 'cool'    = banner roxo    -> header laranja, barra de anúncios roxa
      // 'default' = qualquer outra coisa (mesmas cores do 'warm')
      header.dataset.rxScheme = esquema;
      // No <html> para as seções irmãs (barra de anúncios) reagirem por CSS.
      raiz.setAttribute('data-rx-scheme', esquema);
    };

    const classificar = (cor) => {
      if (!cor) return 'default';
      const dQuente = distancia(cor, alvoQuente);
      const dFrio = distancia(cor, alvoFrio);
      const menor = Math.min(dQuente, dFrio);
      if (menor > tolerancia) return 'default';
      return dQuente < dFrio ? 'warm' : 'cool';
    };

    /**
     * Descobre a imagem de fundo do slide.
     *
     * Cobre os dois tipos de banner:
     *   imagem -> <img class="slide__image">
     *   vídeo  -> <img class="slide__video-poster">, que o tema gera a partir
     *             da miniatura do próprio vídeo (video.preview_image)
     *
     * Por isso a leitura de cor funciona igual nos dois casos, sem precisar
     * abrir o arquivo de vídeo: a miniatura já representa a cena.
     *
     * A busca é feita por classe, e não por um `img` qualquer, porque o slide
     * também pode conter imagens de conteúdo (um selo, um logo) que não têm
     * nada a ver com o fundo.
     */
    const imagemDoSlide = (slide) => {
      if (!slide) return null;

      /*
        CUIDADO COM <picture>.

        Quando o slide tem imagem de mobile separada, o tema renderiza
        art direction:

            <picture class="slide__image">
              <source media="(max-width: 749px)" ...>
              <img class="slide__image" ...>
            </picture>

        A classe aparece nos DOIS elementos. Um querySelector('.slide__image')
        devolve o <picture>, que não tem `src` — daí a necessidade de descer
        até o <img> interno. Sem isso, todo banner com versão mobile deixava
        de ser analisado e o cabeçalho ficava sempre no esquema padrão.
      */
      const seletores = ['.slide__image', '.slide__video-poster', '.slide__image-container img'];

      for (let i = 0; i < seletores.length; i++) {
        const el = slide.querySelector(seletores[i]);
        if (!el) continue;
        const img = el.tagName === 'IMG' ? el : el.querySelector('img');
        if (img && (img.currentSrc || img.src)) return img.currentSrc || img.src;
      }

      return null;
    };

    const avaliarSlide = async (slide) => {
      // Override manual configurado no slide tem prioridade sobre a detecção.
      const manual = slide && slide.dataset ? slide.dataset.rxHeaderScheme : null;
      if (manual && manual !== 'auto') {
        aplicar(manual);
        return;
      }

      const src = imagemDoSlide(slide);
      if (!src) {
        // Vídeo sem miniatura, ou slide sem mídia: não há o que medir.
        aplicar('default');
        return;
      }
      aplicar(classificar(await corMedia(src)));
    };

    /*
      Slide inicial.

      A busca é restrita a slides com `data-rx-header-scheme` — o atributo que
      só os blocos de banner emitem. Sem esse filtro, um carrossel de card de
      produto que apareça antes do banner no DOM seria lido como se fosse ele.
    */
    const primeiroSlide =
      document.querySelector('slideshow-slide[data-rx-header-scheme][aria-hidden="false"]') ||
      document.querySelector('slideshow-slide[data-rx-header-scheme]') ||
      document.querySelector('[data-rx-banner]');

    if (primeiroSlide) {
      avaliarSlide(primeiroSlide);
    } else {
      aplicar('default');
    }

    // O evento do próprio tema borbulha até o document, então não precisamos
    // conhecer nem referenciar o componente de slideshow.
    document.addEventListener('slideshow:select', (event) => {
      const slide = event.detail && event.detail.slide;
      // O evento sobe de QUALQUER slideshow da página — carrossel de card de
      // produto, galeria de mídia do produto. Só o banner manda na cor.
      if (!slide || !slide.hasAttribute('data-rx-header-scheme')) return;
      avaliarSlide(slide);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
